import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import SubjectThumbnail from '@/components/SubjectThumbnail';
import {
  ArrowLeft, Lock, CheckCircle2, Play, BookOpen, Target,
  Trophy, Clock, ChevronRight, Sparkles, Brain, Calculator,
  BookA, Shapes, Zap, Star, AlertCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Subject config
const SUBJECT_CONFIG = {
  maths: { icon: Calculator, color: 'from-blue-600 to-indigo-600', bg: 'bg-blue-500/20', text: 'text-blue-600', name: 'Mathematics' },
  english: { icon: BookA, color: 'from-green-600 to-emerald-600', bg: 'bg-green-500/20', text: 'text-green-600', name: 'English' },
  verbal_reasoning: { icon: Brain, color: 'from-purple-600 to-pink-600', bg: 'bg-purple-500/20', text: 'text-purple-600', name: 'Verbal Reasoning' },
  non_verbal_reasoning: { icon: Shapes, color: 'from-orange-600 to-red-600', bg: 'bg-orange-500/20', text: 'text-orange-600', name: 'Non-Verbal Reasoning' },
  all: { icon: Target, color: 'from-amber-600 to-orange-600', bg: 'bg-amber-500/20', text: 'text-amber-600', name: 'All Subjects' }
};

// Module type config
const MODULE_TYPE_CONFIG = {
  learn: { icon: BookOpen, label: 'Learn', color: 'text-green-600', bg: 'bg-green-500/20' },
  practice: { icon: Target, label: 'Practice', color: 'text-blue-600', bg: 'bg-blue-500/20' },
  challenge: { icon: Zap, label: 'Challenge', color: 'text-purple-600', bg: 'bg-purple-500/20' },
  mock: { icon: Trophy, label: 'Assessment', color: 'text-amber-600', bg: 'bg-amber-500/20' }
};

const WeekDetailView = ({ progress, updateProgress }) => {
  const navigate = useNavigate();
  const { weekNumber } = useParams();
  const weekNum = parseInt(weekNumber);
  const token = localStorage.getItem('token');
  
  // State
  const [weekData, setWeekData] = useState(null);
  const [modules, setModules] = useState([]);
  const [activeModule, setActiveModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch week details from backend
  useEffect(() => {
    fetchWeekDetails();
  }, [weekNum]);

  const fetchWeekDetails = async () => {
    try {
      setLoading(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/learning-path/week/${weekNum}`, { headers });
      
      setWeekData(response.data);
      setModules(response.data.modules || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch week details:', err);
      // Fall back to local progress data
      const weeks = progress?.learningPath?.weeks || [];
      const localWeek = weeks.find(w => w.weekNumber === weekNum);
      if (localWeek) {
        setWeekData(localWeek);
        setModules(localWeek.modules || generateDefaultModules(localWeek));
      } else {
        setError('Week not found');
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate default modules if not present
  const generateDefaultModules = (week) => {
    const focus = week?.focus || week?.focus_subject || 'maths';
    const secondary = focus === 'english' ? 'maths' : 'english';
    return [
      { index: 0, day: 'Monday', title: 'Core Concepts', type: 'learn', subject: focus, duration: 15, completed: false },
      { index: 1, day: 'Tuesday', title: 'Guided Practice', type: 'practice', subject: focus, duration: 15, completed: false },
      { index: 2, day: 'Wednesday', title: 'Skills Building', type: 'learn', subject: secondary, duration: 15, completed: false },
      { index: 3, day: 'Thursday', title: 'Problem Solving', type: 'challenge', subject: focus, duration: 15, completed: false },
      { index: 4, day: 'Friday', title: 'Weekly Assessment', type: 'mock', subject: 'all', duration: 30, completed: false, is_mock: true, locked: true }
    ];
  };

  // Check if all daily modules are complete (for mock unlock)
  const dailyModulesComplete = modules
    .filter(m => m.type !== 'mock' && !m.is_mock)
    .every(m => m.completed);

  // Calculate week progress
  const completedModules = modules.filter(m => m.completed).length;
  const weekProgress = modules.length > 0 ? (completedModules / modules.length) * 100 : 0;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-violet-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading week {weekNum}...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !weekData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center">
        <Card className="bg-white/50 border-slate-200 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Week Not Found</h2>
          <p className="text-slate-500 mb-4">{error || "This week doesn't exist or hasn't been unlocked yet."}</p>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </Card>
      </div>
    );
  }

  const focusSubject = weekData.focus || weekData.focus_subject || 'maths';
  const subjectConfig = SUBJECT_CONFIG[focusSubject] || SUBJECT_CONFIG.maths;

  // Handle module click. Routes to the new curriculum-driven LearningTask
  // (`/learn/<type>/<subject>/<topic-slug>?difficulty=…`) when the API has
  // provided a topic_slug — that's the AI-tutor backed Focus-Mode page that
  // actually teaches the week's intended topic. Falls back to the legacy
  // generic LearningModule route only for very old `learning_path_progress`
  // documents that pre-date the curriculum spec (no topic_slug present).
  const handleModuleClick = (module, index) => {
    if ((module.type === 'mock' || module.is_mock) && (module.locked || !dailyModulesComplete)) {
      toast.error('Complete all daily modules to unlock the weekly assessment');
      return;
    }

    if (module.completed) {
      toast.info('Module already completed. Great job!');
      return;
    }

    const topicSlug = module.topic_slug;
    const subjectKebab = (module.subject || focusSubject || 'maths').replace(/_/g, '-');
    const difficulty = module.target_difficulty || weekData.target_difficulty || 'greater_depth';

    if (topicSlug) {
      // Pick a route "kind" so the LearningTask UI can theme itself
      // (lesson vs practice vs mini-mock).
      let kind = 'revision';
      if (module.kind === 'mock' || module.is_mock) kind = 'quiz';
      else if (module.kind === 'practice') kind = 'practice';
      else if (module.kind === 'exam') kind = 'practice';
      else if (module.kind === 'concept') kind = 'revision';

      navigate(
        `/learn/${kind}/${subjectKebab}/${topicSlug}?difficulty=${encodeURIComponent(difficulty)}`,
        { state: { module, weekNumber: weekNum } }
      );
      return;
    }

    // Legacy fallback for pre-curriculum docs.
    navigate(
      `/learning-path/week/${weekNum}/module/${module.index !== undefined ? module.index : index}`,
      { state: { module, weekNumber: weekNum } }
    );
  };

  // Mark module as complete (called from LearningModule)
  const completeModule = (moduleIndex) => {
    setModules(prev => prev.map((m, i) => 
      i === moduleIndex ? { ...m, completed: true } : m
    ));
    toast.success('Module completed! 🎉');
  };

  return (
    <div 
      data-testid="week-detail-view"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white"
    >
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <div className={`bg-gradient-to-r ${subjectConfig.color} py-8`}>
        <div className="max-w-4xl mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="flex items-center gap-4">
            {/* Hero subject illustration — replaces the plain icon block with
                the lively chalkboard / book / alphabet thumbnail. */}
            <div className="p-2 rounded-2xl bg-white/15">
              <SubjectThumbnail subject={focusSubject} size="lg" animate={false} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                  Week {weekNum}
                </h1>
                <Badge className="bg-slate-100 text-slate-900">
                  {subjectConfig.name} Focus
                </Badge>
              </div>
              <p className="text-slate-600">
                Complete all modules to unlock the weekly assessment
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-600">Week Progress</span>
              <span className="text-slate-900 font-medium">{Math.round(weekProgress)}%</span>
            </div>
            <Progress value={weekProgress} className="h-3 bg-slate-100" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8">
        {/* Daily Modules */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-600" />
            Daily Learning Modules
          </h2>

          {modules.map((module, index) => {
            const typeConfig = MODULE_TYPE_CONFIG[module.type] || MODULE_TYPE_CONFIG.learn;
            const TypeIcon = typeConfig?.icon || BookOpen;
            const moduleSubject = SUBJECT_CONFIG[module.subject] || SUBJECT_CONFIG.all;
            
            const isLocked = (module.type === 'mock' || module.is_mock) && (module.locked || !dailyModulesComplete);
            const isCompleted = module.completed;
            const isCurrent = !isCompleted && !isLocked && 
              modules.slice(0, index).filter(m => m.type !== 'mock').every(m => m.completed);

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card
                  data-testid={`module-${index}`}
                  className={`transition-all cursor-pointer ${
                    isLocked 
                      ? 'bg-white/30 border-slate-200 opacity-60' 
                      : isCompleted
                        ? 'bg-green-900/20 border-green-500/30'
                        : isCurrent
                          ? 'bg-violet-900/20 border-violet-500/50 ring-2 ring-violet-500/30'
                          : 'bg-white/50 border-slate-200 hover:border-slate-200'
                  }`}
                  onClick={() => handleModuleClick(module, index)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Day Badge */}
                      <div className={`w-16 h-16 rounded-xl flex flex-col items-center justify-center ${
                        isCompleted ? 'bg-green-500/20' : isLocked ? 'bg-slate-100' : typeConfig.bg
                      }`}>
                        <span className={`text-xs font-medium ${
                          isCompleted ? 'text-green-600' : isLocked ? 'text-slate-500' : typeConfig.color
                        }`}>
                          {module.day}
                        </span>
                        {isCompleted ? (
                          <CheckCircle2 className="w-6 h-6 text-green-600 mt-1" />
                        ) : isLocked ? (
                          <Lock className="w-6 h-6 text-slate-500 mt-1" />
                        ) : (
                          <TypeIcon className={`w-6 h-6 ${typeConfig.color} mt-1`} />
                        )}
                      </div>

                      {/* Subject illustration — lively per-day thumbnail. */}
                      {!isLocked && (
                        <SubjectThumbnail subject={module.subject || focusSubject} size="md" animate={false} />
                      )}

                      {/* Module Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`font-semibold ${isLocked ? 'text-slate-500' : 'text-slate-900'}`}>
                            {module.title}
                          </h3>
                          {isCurrent && (
                            <Badge className="bg-violet-500 text-white text-xs">
                              Current
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-3 text-sm">
                          <div className="flex items-center gap-1">
                            <span className={isLocked ? 'text-slate-500' : 'text-slate-500'}>
                              {moduleSubject.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className={`w-4 h-4 ${isLocked ? 'text-slate-500' : 'text-slate-500'}`} />
                            <span className={isLocked ? 'text-slate-500' : 'text-slate-500'}>
                              {module.type === 'mock' ? '30 min' : '15 min'}
                            </span>
                          </div>
                        </div>

                        {module.type === 'mock' && !dailyModulesComplete && (
                          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Complete all daily modules to unlock
                          </p>
                        )}
                      </div>

                      {/* Action */}
                      <div>
                        {isCompleted ? (
                          <div className="flex items-center gap-2 text-green-600">
                            <Star className="w-5 h-5" />
                            <span className="text-sm font-medium">Done</span>
                          </div>
                        ) : isLocked ? (
                          <Lock className="w-5 h-5 text-slate-500" />
                        ) : (
                          <Button 
                            size="sm" 
                            className={`bg-gradient-to-r ${
                              module.type === 'mock' 
                                ? 'from-amber-600 to-orange-600' 
                                : 'from-violet-600 to-indigo-600'
                            }`}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            {module.type === 'mock' ? 'Take Test' : 'Start'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Week Summary Card */}
        {weekProgress === 100 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8"
          >
            <Card className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 border-green-500/30">
              <CardContent className="p-6 text-center">
                <Trophy className="w-12 h-12 text-amber-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">Week {weekNum} Complete! 🎉</h3>
                <p className="text-slate-600 mb-4">
                  Congratulations! You've completed all modules for this week. Week {weekNum + 1} is now unlocked.
                </p>
                <Button 
                  onClick={() => navigate('/learning-path')}
                  className="bg-gradient-to-r from-green-600 to-emerald-600"
                >
                  Continue to Week {weekNum + 1}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default WeekDetailView;
